import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { LiveTracker } from 'src/domains/sportsbook/webview/components/scoreboards/liveTracker/LiveTracker';
import { LSportWrapper } from 'src/domains/sportsbook/webview/components/scoreboards/lSport/lSport';
import { Scoreboards } from 'src/domains/sportsbook/webview/components/scoreboards/Scoreboards';
import { ImgArena } from 'src/domains/sportsbook/webview/components/scoreboards/imgArena/ImgArena';
import { GeniusWidgets } from 'src/domains/sportsbook/webview/components/scoreboards/genius/GeniusWidgets';
import { getGeniusWidgetConfig } from 'src/domains/sportsbook/webview/components/scoreboards/genius/getGeniusWidgetConfig';
import { GeniusLivePlayer } from 'src/domains/sportsbook/webview/components/stream/genius';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { SdkCustomer } from 'src/domains/layouts/state/customer';
import { MatchLeadContainer } from './MatchLead.style';
import { MatchLeadState } from './MatchLeadState';
import { MatchLeadViewToggle } from './MatchLeadViewToggle';
import { MatchLeadStreamGate } from './MatchLeadStreamGate';

interface MatchLeadPropsType {
    event: EventModel | null;
}

export const MatchLead = observer('MatchLead', ({ event }: MatchLeadPropsType) => {
    const common = useCommon();
    const config = ConfigComponents.get(common).config;
    const [state] = useState(() => new MatchLeadState());

    const eventId = event?.id2.toOldId() ?? null;
    const isAuthorized = common.session.isAuthorized === true;
    const userIpCountry = SdkCustomer.get(common).session.userIpCountry;
    const isGeniusSport = event !== null && config.geniusSports.some((sport) => sport === event.sport);
    const isStreamingSport =
        event !== null &&
        config.geniusStreamingSports !== null &&
        config.geniusStreamingSports.some((sport) => sport === event.sport);
    const showStreamToggle =
        config.geniusStreamingEnabled === true &&
        isGeniusSport === true &&
        isStreamingSport === true &&
        event?.timeMatchInPlay === true &&
        eventId !== null;
    const isWatchLiveView = showStreamToggle === true && state.activeView === 'watchLive';

    const clientGate = event !== null ? state.resolveClientGate(event, isAuthorized, userIpCountry) : 'unauthenticated';
    const shouldMountPlayer = isWatchLiveView === true && clientGate === 'ready' && state.playerGateReason === null;

    useEffect(() => {
        state.syncPlayerState(common, eventId, shouldMountPlayer);
    }, [state, common, eventId, shouldMountPlayer]);

    const playerState = state.playerState;
    const metadataResultType = playerState?.metadataResult.type;

    useEffect(() => {
        if (playerState === null) {
            return;
        }

        playerState.syncUiStatusFromMetadata();
    }, [playerState, metadataResultType]);

    if (event === null || eventId === null) {
        return null;
    }

    const widgetConfig = getGeniusWidgetConfig(event, config);
    const hasGeniusGameTracker = widgetConfig?.kind === 'gameTracker';

    const hasGeniusWidget = widgetConfig !== null;
    const showOverviewContent = isWatchLiveView === false;
    const gateReason = state.playerGateReason ?? (clientGate === 'ready' ? null : clientGate);
    const showStreamGate = isWatchLiveView === true && gateReason !== null;
    const showGeniusPlayer = shouldMountPlayer === true && playerState !== null && state.playerGateReason === null;

    return (
        <MatchLeadContainer>
            {showStreamToggle === true ? (
                <MatchLeadViewToggle
                    activeView={state.activeView}
                    onChange={state.setActiveView}
                />
            ) : null}

            {showStreamGate === true && gateReason !== null ? (
                <MatchLeadStreamGate
                    event={event}
                    reason={gateReason}
                />
            ) : null}

            {showGeniusPlayer === true && playerState !== null ? (
                <GeniusLivePlayer
                    key={eventId}
                    eventId={eventId}
                    state={playerState}
                    onStreamUnavailable={state.handleStreamUnavailable}
                />
            ) : null}

            {showOverviewContent === true ? (
                <>
                    {/* Basic / animated scoreboards — hidden when Genius widget is shown */}
                    {hasGeniusWidget === false ? <Scoreboards event={event} /> : null}

                    {/* Genius Scoreboard / Game Tracker */}
                    <GeniusWidgets event={event} />

                    {/* LSport now is Statscore scoreboard */}
                    <LSportWrapper
                        sport={event.sport}
                        lsportExternalId={event.lsportExternalId}
                        timeSettingsStarted={event.timeSettingsStarted}
                    />

                    {/* Live Tracker from Planetsport with animation */}
                    {event.template === 'outrights' || hasGeniusGameTracker === true ? null : (
                        <LiveTracker
                            sport={event.sport}
                            eventId={eventId}
                            isOpen={event.timeMatchInPlay}
                        />
                    )}

                    {/* Img arena live tracer */}
                    <ImgArena
                        sport={event.sport}
                        eventFeedGroupId={event.feedGroupId} // hardcoded event for stg testing: '644'
                        eventId={eventId}
                    />
                </>
            ) : null}
        </MatchLeadContainer>
    );
});
